import { useCallback, useEffect, useRef, useState } from "react";
import useToast from "~/components/ui/toast";
import {
  cancelConversation,
  type ConversationConnect,
  conversationConnectPath,
} from "~/services/conversations";
import { apiClient } from "~/utils/apiClient";
import type { ReplayHttpAgent } from "./ReplayHttpAgent";
import { danglingToolCalls, type PendingInput, pendingInputOf } from "./awaitingInput";
import type { Turn } from "./useTurns";

// Ceiling on consecutive self-heal attempts after a dropped stream, so a stream
// that dies the instant it opens can't spin /connect forever. Reset when a run
// starts.
const MAX_REATTACH_ATTEMPTS = 3;

export interface RunReconcile {
  // True from a cancel request until /connect says what the server kept.
  isCancelling: boolean;
  // True while refetching /connect after a dropped or interrupted run.
  reconciling: boolean;
  // The server's word on what the conversation is waiting on, as of the last
  // /connect.
  pending: PendingInput;
  cancel: () => Promise<void>;
  // Call when the user starts a turn: it supersedes an in-flight cancel
  // reconcile, whose snapshot would otherwise overwrite the new turn.
  supersedeCancel: () => void;
}

/**
 * Keeps a live conversation in step with the server's run.
 *
 * The SSE stream alone can't tell finished from cancelled, crashed or dropped,
 * so after a cancel, a transport failure, or a run that ends on a dangling tool
 * call, this adopts `/connect` (messages and pending input), re-attaching if the run is still alive there.
 * It also attaches to an in-flight run on mount and tears the stream down on
 * unmount.
 *
 * Args:
 *   agent: The conversation's agent.
 *   conversationId: The conversation `/connect` is fetched for.
 *   turns: The rendered turns, read for dangling tool calls.
 *   resumeInFlight: Whether `/connect` reported a run in flight at mount.
 *   initialPending: What `/connect` reported the conversation waiting on at mount.
 */
export function useRunReconcile(
  agent: ReplayHttpAgent,
  conversationId: string,
  turns: readonly Turn[],
  resumeInFlight: boolean,
  initialPending: PendingInput
): RunReconcile {
  const { showError } = useToast();
  const [isCancelling, setIsCancelling] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [dropReattachAttempts, setDropReattachAttempts] = useState(0);
  const [pending, setPending] = useState<PendingInput>(initialPending);
  const prevRunningRef = useRef(false);
  const hasDanglingCalls = danglingToolCalls(turns).length > 0;

  useEffect(() => {
    if (resumeInFlight) {
      agent.enableReplayForNextRun();
      void agent.runAgent();
    }
    return () => {
      agent.abortRun();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount only
  }, []);

  const reconcileFromConnect = useCallback(
    async (signal: AbortSignal) => {
      const data = await apiClient<ConversationConnect>(conversationConnectPath(conversationId), {
        signal,
      });
      if (signal.aborted) return;
      agent.setMessages(data.messages);
      setPending(pendingInputOf(data));
      if (data.status === "MESSAGE_IN_PROGRESS") {
        agent.enableReplayForNextRun();
        void agent.runAgent();
      }
    },
    [agent, conversationId]
  );

  // The analysis-agent closes the stream the same way on cancel as on normal
  // completion, so only /connect says whether the cancel landed.
  useEffect(() => {
    if (agent.isRunning || !isCancelling) return;
    const controller = new AbortController();
    reconcileFromConnect(controller.signal)
      .catch((err) => {
        if (controller.signal.aborted) return;
        if (err instanceof DOMException && err.name === "AbortError") return;
        console.error("Failed to reconcile conversation after cancel", err);
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsCancelling(false);
      });
    return () => controller.abort();
  }, [agent, agent.isRunning, isCancelling, reconcileFromConnect]);

  // A run that ends on a dangling tool call either paused for input or was
  // interrupted mid-call; /connect says which.
  useEffect(() => {
    const wasRunning = prevRunningRef.current;
    prevRunningRef.current = agent.isRunning;
    if (wasRunning && !agent.isRunning && !isCancelling && hasDanglingCalls) {
      setReconciling(true);
    }
  }, [agent.isRunning, isCancelling, hasDanglingCalls]);

  // A stream killed at the transport layer ends with no AG-UI event at all.
  // Aborts never reach `onRunFailed`: `ReplayHttpAgent` drops errors once its
  // own signal has fired.
  useEffect(() => {
    const sub = agent.subscribe({
      onRunStartedEvent: () => setDropReattachAttempts(0),
      onRunFailed: () => {
        if (isCancelling || dropReattachAttempts >= MAX_REATTACH_ATTEMPTS) return;
        setDropReattachAttempts((attempts) => attempts + 1);
        setReconciling(true);
      },
    });
    return () => sub.unsubscribe();
  }, [agent, isCancelling, dropReattachAttempts]);

  useEffect(() => {
    if (!reconciling) return;
    const controller = new AbortController();
    reconcileFromConnect(controller.signal)
      .catch((err) => {
        if (controller.signal.aborted) return;
        if (err instanceof DOMException && err.name === "AbortError") return;
        console.error("Failed to reconcile after run ended", err);
      })
      .finally(() => {
        if (!controller.signal.aborted) setReconciling(false);
      });
    return () => controller.abort();
  }, [reconciling, reconcileFromConnect]);

  const cancel = useCallback(async () => {
    // A repeat Stop hits a run the server has already unwound, and its failure
    // would clear `isCancelling` — skipping the first cancel's reconcile.
    if (isCancelling) return;
    setIsCancelling(true);
    try {
      await cancelConversation(conversationId);
    } catch (err) {
      // The server-side run is still going; let the user try again.
      setIsCancelling(false);
      console.error("Failed to cancel conversation", err);
      showError("Couldn't stop generation. Try again in a moment.");
    }
  }, [isCancelling, conversationId, showError]);

  const supersedeCancel = useCallback(() => setIsCancelling(false), []);

  return { isCancelling, reconciling, pending, cancel, supersedeCancel };
}
